import { ComponentProps } from 'react';
import { useDragLayer } from 'react-dnd';

import styled, { css } from '@mint-lib/styled';

import { Icon, Truncation } from '@myn/mui';

import { dragItemTypes } from '../../../constants/myCampaigns.js';
import { Offset } from '../../../types/shared.js';

type Props = {
  resize?: { [name: string]: number };
  className?: string;
  scale?: number;
};

const DragLayer = ({ resize, className, scale = 1 }: Props) => {
  const { item, currentOffset, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (
    !isDragging ||
    !currentOffset ||
    ![dragItemTypes.HEADER, dragItemTypes.EXIST_HEADER].includes(item?.type)
  ) {
    return null;
  }

  return (
    <Layer className={className}>
      <CardStyled
        currentOffset={currentOffset}
        width={resize?.[item.id] || item.width}
        $scale={scale}
      >
        <Icon
          component="Draggable"
          sx={{ mr: 1, width: `${scale}rem`, height: `${scale}rem` }}
        />
        <StyledTruncation variant="h6" $scale={scale}>
          {item.name}
        </StyledTruncation>
      </CardStyled>
    </Layer>
  );
};

const Layer = styled('div')`
  position: fixed;
  z-index: 999;
  left: 0;
  top: 0;
  width: 100%;
  min-width: 120px;
  height: 100%;
  cursor: grab;
  border-radius: 4px;
`;

const CardStyled = styled(
  ({
    currentOffset,
    width,
    $scale,
    ...props
  }: ComponentProps<'div'> & {
    currentOffset: Offset;
    width: string;
    $scale: number;
  }) => (
    // @ts-ignore TODO: fix type
    <div style={{ top: currentOffset.y, left: currentOffset.x }} {...props} />
  ),
)`
  ${({ width, $scale, theme }) => css`
    position: absolute;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    padding: 0 ${theme.spacing(2)} 0 ${theme.spacing(0.875)};
    border: 1px solid ${theme.palette.silver.main};
    background-color: ${theme.palette.ui.primary};
    width: ${Number(width) * $scale}px;
    height: ${40 * $scale}px;
    cursor: grabbing;
    opacity: 0.75;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.25);
  `}
`;

const StyledTruncation = styled(Truncation)<{ $scale: number }>`
  ${({ $scale }) => css`
    text-align: right;
    line-height: 1;
    font-size: ${0.875 * $scale}rem;
  `}
`;

export default DragLayer;
